import {
  Control,
  Icon,
  LatLngBounds,
  LayerGroup,
  LeafletMouseEvent,
  Map,
  Marker,
  TileLayer,
}                                    from 'leaflet';
import store                         from '@/store';
import IPosition                     from '@/interfaces/IPosition';
import GeolocationService            from '@/services/GeolocationService';
import { IFeatureCollection }        from '@/interfaces/IFeatureCollection';
// eslint-disable-next-line import/no-extraneous-dependencies
import '@/../node_modules/prunecluster/dist/LeafletStyleSheet.css';
import ParkingLayerService           from '@/services/layers/ParkingsLayerService';
import ParkingZoneLayerService       from '@/services/layers/ParkingZoneLayerService';
import ParkingZonePointsLayerService from '@/services/layers/ParkingZonePointsLayerService';
import ParkingMetersLayerService     from '@/services/layers/ParkingMetersLayerService';
import ParkingSpotsService           from '@/services/layers/ParkingSpotsService';
import ParkingGroupsLayerService     from '@/services/layers/ParkingGroupsLayerService';
import EventsLayerService            from '@/services/layers/EventsLayerService';
import WeatherStationsLayerService   from '@/services/layers/WeatherStationsLayerService';

import Zoom = Control.Zoom;

/**
 * A class for abstracting away manipulations of leaflet map instance;
 */
export default class MapService {
  /**
   * Map an instance of leaflet
   */
  private mapReference: Map;

  /**
   *
   * @param leafletMapInstance Map an instance of leaflet that will be manipulated using this class
   */
  constructor(leafletMapInstance: Map) {
    this.mapReference = leafletMapInstance;
  }

  /**
   * Initialises the map, should be fired only once per map instance.
   */
  public initializeMap = (): void => {
    this.setTileLayer();
    this.setBounds();
    this.bindEvents();

    this.mapReference.addControl(new Zoom({
      position: 'topright',
    }));
  };

  /**
   * Binds store updating events
   */
  public bindEvents = (): void => {
    this.mapReference.on('moveend', (event) => {
      store.commit('updatePosition', event.target.getCenter());
    });

    this.mapReference.on('zoomend', (event) => {
      store.commit('updateZoomFactor', event.target.getZoom());
    });
  };

  /**
   * Sets the provider of map tiles
   */
  private setTileLayer = (): void => {
    const tileLayer = new TileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxNativeZoom: 19,
      maxZoom: 21,
      minZoom: 12,
    });
    this.mapReference.addLayer(tileLayer);
  };

  /**
   * Sets the position and the zoom factor of the map.
   * @param {IPosition} position Lat and lng of the map view center.
   * @param {number} zoom Zoom factor of the map.
   */
  public setPostition = (position: IPosition, zoom: number = 13): void => {
    this.mapReference.setView([position.lat, position.lng], zoom);
  };

  /**
   * Sets a geolocation marker on the map using set position.
   * @param {IPosition} position Position at which the marker will be set.
   */
  public setGeolocationPositionMarker = (position: IPosition): Marker => {
    const positionMarker = new Marker([position.lat, position.lng], {
      icon: new Icon({
        // eslint-disable-next-line global-require
        iconUrl: require('@/assets/markers/icon-map-position.svg'),
        iconSize: [40, 40],
        iconAnchor: [20, 20],
        popupAnchor: [-3, -76],
      }),
    });
    this.mapReference.addLayer(positionMarker);

    return positionMarker;
  };

  /**
   * Sets a marker on the map showing the selected position.
   * @param {IPosition} position Position at which the marker will be set.
   */
  public setSelectedPositionMarker = (position: IPosition): Marker => {
    const positionMarker = new Marker([position.lat, position.lng], {
      icon: new Icon({
        // eslint-disable-next-line global-require
        iconUrl: require('@/assets/markers/marker-location.svg'),
        iconSize: [40, 40],
        iconAnchor: [20, 20],
        popupAnchor: [-3, -76],
      }),
    });
    // this.mapReference.addLayer(positionMarker);

    return positionMarker;
  };

  /**
   * Draws parking zones center markers on the map based on vuex store data.
   */
  public drawParkingZonesMarkers = (): LayerGroup => {
    const parkingZonePointsLayerService: ParkingZonePointsLayerService =
      new ParkingZonePointsLayerService();
    const parkingZonesMarkersLayer: LayerGroup = parkingZonePointsLayerService.redrawLayer();
    this.mapReference.addLayer(parkingZonesMarkersLayer);
    return parkingZonesMarkersLayer;
  };

  /**
   * Sets the default boundaries of the map.
   */
  public setBounds = (): void => {
    const mapBounds = new LatLngBounds([
      [50.126674, 21.077248],
      [49.926674, 20.877248],
    ]);

    this.mapReference.setMaxBounds(mapBounds);
    this.mapReference.on('drag', () => {
      this.mapReference.panInsideBounds(mapBounds, { animate: false });
    });
  };

  /**
   * Sets the position marker from using geolocation data.
   */
  public setPositionFromGeolocation = (): Promise<any> => GeolocationService.getCurrentPosition()
    .then((value) => {
      const geolocationPositionMarker: Marker = this.setSelectedPositionMarker({
        lat: value.coords.latitude,
        lng: value.coords.longitude,
      });
      this.setMarkerAndEndPositionSetMode(geolocationPositionMarker);
    });

  /**
   * Initialises the position set mode for the leaflet map.
   */
  public startPositionSetMode = (): void => {
    this.mapReference.on('click', (event: LeafletMouseEvent) => {
      const userPositionMarker: Marker = this.setSelectedPositionMarker(event.latlng);
      // this.setMarkerAndEndPositionSetMode(userPositionMarker);
    });
  };

  /**
   * Ends the manual position setting mode on leaflet map.
   */
  public endPositionSetMode = (): void => {
    this.mapReference.off('click');
  };

  /**
   * Updates the position marker in the store and ends manual position setting mode.
   * @param {Marker} marker Marker to be pushed to the store.
   */
  private setMarkerAndEndPositionSetMode = (marker: Marker): void => {
    store.state.userPositionMarker.remove();
    store.commit('updateUserPositionMarker', marker);
    store.commit('endLocationMode');
    this.endPositionSetMode();
  };

  /**
   * Updates the map with the marker data from the store.
   */
  public drawParkingsMarkers = (): LayerGroup => {
    const parkingLayerService: ParkingLayerService = new ParkingLayerService();
    const parkingsMarkersLayer: LayerGroup = parkingLayerService.redrawLayer();
    this.mapReference.addLayer(parkingsMarkersLayer);
    return parkingsMarkersLayer;
  };

  /**
   * Draws parking zones on the map based on vuex store data.
   */
  public drawParkingZonesPolygons = (): LayerGroup => {
    const parkingZoneLayerService = new ParkingZoneLayerService();
    const parkingZonesPolygonsLayer = parkingZoneLayerService.redrawLayer();
    this.mapReference.addLayer(parkingZonesPolygonsLayer);
    return parkingZonesPolygonsLayer;
  };

  /**
   * Draws parking zones on the map based on vuex store data.
   */
  public drawParkingMetersMarkers = (): LayerGroup => {
    const parkingMetersLayerService = new ParkingMetersLayerService();
    const parkingMetersPolygonsLayer = parkingMetersLayerService.redrawLayer();
    this.mapReference.addLayer(parkingMetersPolygonsLayer);
    return parkingMetersPolygonsLayer;
  };

  /**
   * Draws parking zones on the map based on vuex store data.
   */
  public drawParkingSpotsMarkers = (): LayerGroup => {
    const parkingSpotsService = new ParkingSpotsService();
    const parkingSpotsMarkersLayer = parkingSpotsService.redrawLayer();
    this.mapReference.addLayer(parkingSpotsMarkersLayer);
    return parkingSpotsMarkersLayer;
  };

  /**
   * Draws parking zones on the map based on vuex store data.
   */
  public drawParkingGroupsPolygons = (): LayerGroup => {
    const parkingGroupsLayerService = new ParkingGroupsLayerService();
    const parkingGroupsPolygonsLayer = parkingGroupsLayerService.redrawLayer();
    this.mapReference.addLayer(parkingGroupsPolygonsLayer);
    return parkingGroupsPolygonsLayer;
  };

  /**
   * Draws events on the map based on the vuex store data and selected category.
   * @param category {string} event category to be rendered
   */
  public drawEventsMarkers = (category: string = 'OTHER'): LayerGroup => {
    const eventsLayerService = new EventsLayerService();
    const eventsLayer = eventsLayerService.redrawLayer(category);
    this.mapReference.addLayer(eventsLayer);
    return eventsLayer;
  };

  /**
   * Draws parking zones on the map based on vuex store data.
   */
  public drawWeatherStationsMarkers = (): LayerGroup => {
    const weatherStationsLayerService = new WeatherStationsLayerService();
    const weatherStationsPolygonsLayer = weatherStationsLayerService.redrawLayer();
    this.mapReference.addLayer(weatherStationsPolygonsLayer);
    return weatherStationsPolygonsLayer;
  };
}
